<script>
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useProductCatalogLauncher } from 'dashboard/composables/useProductCatalogLauncher';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import TaskComposer from './TaskComposer.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
} from 'dashboard/helper/editorHelper';
import types from 'dashboard/store/mutation-types';
import ConversationApi from 'dashboard/api/inbox/conversation';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    EmojiInput,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    TaskComposer,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox'],
  setup() {
    const {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();
    const { requestCatalogAction } = useProductCatalogLauncher();

    const replyEditor = useTemplateRef('replyEditor');

    return {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      requestCatalogAction,
      replyEditor,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      taskDueAt: null,
      taskNote: '',
      taskAssignee: null,
      isSavingTask: false,
      isTaskCompleting: false,
      completedTaskDisplay: null,
      dismissedCopilotSuggestionKey: '',
      appliedCopilotSuggestionKeys: [],
      aiCopilotSuggestion: null,
      aiCopilotSuggestionSource: null,
      isFetchingCopilotSuggestion: false,
      isGeneratingManualReplySuggestion: false,
      fetchCopilotSuggestionDebounced: () => {},
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      agentList: 'agents/getAgents',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      accountLabels: 'labels/getLabels',
      macros: 'macros/getMacros',
      cannedResponses: 'getCannedResponses',
    }),
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        !this.isOnTaskMode &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isATwitterInbox) return true;
      if (this.isOnTaskMode) {
        return (
          this.isSavingTask ||
          !this.taskDueAt ||
          !this.taskNote?.trim() ||
          this.taskNote.length > this.maxLength
        );
      }
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isOnTaskMode) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.TASK_FORM.SAVE');
      } else if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'is-task-mode': this.isOnTaskMode,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && !this.isOnTaskMode && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    lastCustomerMessage() {
      const messages = this.currentChat?.messages || [];
      return [...messages].reverse().find(message => {
        return message.sender_type === 'Contact' || message.message_type === 0;
      });
    },
    lastCustomerMessageText() {
      const content = this.lastCustomerMessage?.content || '';
      return content
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    copilotRequestKey() {
      const lastMessageId = this.currentChat?.messages?.at?.(-1)?.id || 0;
      return this.currentChat?.id
        ? `${this.currentChat.id}-${lastMessageId}`
        : '';
    },
    hasCatalogIntent() {
      return /produto|servi[cç]o|cat[aá]logo|comprar|pedido|or[cç]amento|valor|pre[cç]o|dispon[ií]vel|tem .*estoque/i.test(
        this.lastCustomerMessageText
      );
    },
    hasPaymentIntent() {
      return /pix|pagamento|pagar|boleto|cart[aã]o|link|cobran[cç]a|parcel|transfer[eê]ncia/i.test(
        this.lastCustomerMessageText
      );
    },
    hasFollowUpIntent() {
      return /retorno|amanh[aã]|depois|lembra|avisar|acompanhar|follow|semana que vem|mais tarde|me chama|me avisa/i.test(
        this.lastCustomerMessageText
      );
    },
    hasTeamIntent() {
      return /financeiro|entrega|estoque|gerente|suporte|time|equipe|respons[aá]vel|separa|expedi[cç][aã]o|t[ée]cnico/i.test(
        this.lastCustomerMessageText
      );
    },
    hasClosureIntent() {
      return /obrigad|valeu|perfeito|resolvido|deu certo|tudo certo|ok obrigado/i.test(
        this.lastCustomerMessageText
      );
    },
    hasUrgentIntent() {
      return /urgente|agora|r[aá]pido|imediato|prioridade|reclama[cç][aã]o|problema grave|cancelar/i.test(
        this.lastCustomerMessageText
      );
    },
    hasSchedulingIntent() {
      return /agenda|agendar|hor[aá]rio|marcar|consulta|atendimento|visita|reuni[aã]o|amanh[aã]|hoje|segunda|ter[cç]a|quarta|quinta|sexta|s[aá]bado/i.test(
        this.lastCustomerMessageText
      );
    },
    detectedContactFields() {
      const text = this.lastCustomerMessageText;
      const fields = [];
      if (/[\w.+-]+@[\w-]+\.[\w.-]+/.test(text)) fields.push('e-mail');
      if (/(?:\+?55\s?)?(?:\(?\d{2}\)?\s?)?9?\d{4}[-\s]?\d{4}/.test(text)) {
        fields.push('telefone');
      }
      if (/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/.test(text)) fields.push('CPF');
      if (/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/.test(text)) {
        fields.push('CNPJ');
      }
      if (/https?:\/\/\S+|www\.\S+/i.test(text)) fields.push('link');
      return fields;
    },
    matchingPixCannedResponse() {
      return (this.cannedResponses || []).find(response => {
        const search = `${response.short_code || ''} ${response.content || ''}`;
        return /pix|pagamento|pagar|chave/i.test(search);
      });
    },
    matchingMacro() {
      const text = this.lastCustomerMessageText;
      const candidates = [
        {
          pattern: /rastreio|c[oó]digo de rastreio|entrega|enviado/i,
          keywords: ['rastre', 'entrega'],
        },
        {
          pattern: /cobran[cç]a|pagar|pagamento|pix|boleto/i,
          keywords: ['cobran', 'pagamento', 'pix'],
        },
        {
          pattern: /agendar|agenda|hor[aá]rio|lembrete/i,
          keywords: ['agenda', 'lembrete'],
        },
      ];
      const match = candidates.find(candidate => candidate.pattern.test(text));
      if (!match) return null;

      return (this.macros || []).find(macro => {
        const name = `${macro.name || macro.title || ''}`.toLowerCase();
        return match.keywords.some(keyword => name.includes(keyword));
      });
    },
    matchingLabel() {
      const currentLabels = this.currentChat?.labels || [];
      const text = this.lastCustomerMessageText;
      const candidates = [
        {
          pattern: /pre[cç]o|or[cç]amento|valor|comprar|pedido/i,
          keywords: ['negociação', 'negociacao', 'lead', 'cliente'],
        },
        {
          pattern: /pagamento|pix|pagar|boleto|cart[aã]o/i,
          keywords: [
            'pagamento',
            'cobrança',
            'cobranca',
            'negociação',
            'negociacao',
          ],
        },
        {
          pattern: /resolvido|obrigad|deu certo|tudo certo/i,
          keywords: ['cliente', 'concluído', 'concluido'],
        },
      ];
      const match = candidates.find(candidate => candidate.pattern.test(text));
      if (!match) return null;

      return (this.accountLabels || []).find(label => {
        const title = `${label.title || ''}`;
        const normalizedTitle = title.toLowerCase();
        return (
          !currentLabels.includes(title) &&
          match.keywords.some(keyword => normalizedTitle.includes(keyword))
        );
      });
    },
    copilotSuggestionKey() {
      const suggestion = this.copilotSuggestionCandidate;
      return suggestion
        ? `${this.conversationId}-${this.lastCustomerMessage?.id || suggestion.messageId}-${suggestion.type}`
        : '';
    },
    shouldShowCopilotSuggestion() {
      return Boolean(
        this.copilotSuggestionCandidate &&
        this.copilotSuggestionKey !== this.dismissedCopilotSuggestionKey &&
        !this.appliedCopilotSuggestionKeys.includes(
          this.copilotSuggestionKey
        ) &&
        !this.showAudioRecorderEditor
      );
    },
    copilotSuggestionCandidate() {
      if (this.aiCopilotSuggestion) return this.aiCopilotSuggestion;

      return this.deterministicCopilotSuggestionCandidate;
    },
    deterministicCopilotSuggestionCandidate() {
      if (!this.currentChat?.id || !this.lastCustomerMessageText) return null;

      if (this.hasUrgentIntent && this.currentChat.priority !== 'urgent') {
        return {
          type: 'set_urgent_priority',
          title: 'Marcar como prioridade urgente',
          description:
            'A mensagem indica pressa ou risco de perda. Posso destacar essa conversa como urgente.',
        };
      }

      if (this.detectedContactFields.length) {
        return {
          type: 'open_contact_profile',
          title: 'Revisar campos do contato',
          description: `Detectei ${this.detectedContactFields.join(', ')}. Abra o perfil para conferir e preencher os campos.`,
        };
      }

      if (this.matchingMacro) {
        return {
          type: 'execute_macro',
          title: `Executar automação ${this.matchingMacro.name || this.matchingMacro.title}`,
          description:
            'Existe uma automação cadastrada que combina com essa mensagem. Posso aplicá-la nesta conversa.',
        };
      }

      if (this.matchingLabel) {
        return {
          type: 'assign_label',
          title: `Mover para ${this.matchingLabel.title}`,
          description:
            'A mensagem combina com uma etapa do funil. Posso atualizar a etiqueta da conversa.',
        };
      }

      if (this.hasFollowUpIntent && !this.currentChat.active_task) {
        return {
          type: 'create_task',
          title: 'Criar tarefa de follow-up',
          description:
            'O cliente indicou retorno ou acompanhamento. Posso preparar uma tarefa para amanhã.',
          actionLabel: 'Criar tarefa',
        };
      }

      if (this.hasPaymentIntent) {
        return {
          type: 'quick_reply_pix',
          title: this.matchingPixCannedResponse
            ? 'Usar resposta rápida de PIX'
            : 'Sugerir resposta de pagamento',
          description: this.matchingPixCannedResponse
            ? `Vou inserir /${this.matchingPixCannedResponse.short_code} para responder com os dados de pagamento.`
            : 'Posso preparar uma resposta curta oferecendo PIX ou link de pagamento.',
        };
      }

      if (this.hasCatalogIntent) {
        return {
          type: 'create_cart',
          title: 'Montar carrinho para o cliente',
          description:
            'A conversa menciona produto, preço ou orçamento. Posso abrir o carrinho/catálogo para montar o pedido.',
        };
      }

      if (this.hasSchedulingIntent && !this.currentChat.active_task) {
        return {
          type: 'create_schedule_task',
          title: 'Criar tarefa de agendamento',
          description:
            'O cliente falou sobre data ou horário. Posso preparar uma tarefa para organizar esse retorno.',
        };
      }

      if (this.hasTeamIntent && !this.isOnPrivateNote) {
        return {
          type: 'team_note',
          title: 'Mencionar o time internamente',
          description:
            'Parece depender de outra área. Posso preparar uma nota privada para a equipe.',
        };
      }

      if (
        this.hasClosureIntent &&
        this.currentChat.status === wootConstants.STATUS_TYPE.OPEN
      ) {
        return {
          type: 'resolve_conversation',
          title: 'Resolver conversa',
          description:
            'O cliente parece ter encerrado o atendimento. Posso marcar a conversa como resolvida.',
        };
      }

      if (this.currentChat.status === wootConstants.STATUS_TYPE.OPEN) {
        return {
          type: 'reply_suggestion',
          title: 'Sugerir resposta ao cliente',
          description:
            'Posso rascunhar uma resposta curta considerando a última mensagem recebida.',
        };
      }

      return null;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnTaskMode() {
      return this.replyType === REPLY_EDITOR_MODES.TASK;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return (
        !this.isOnPrivateNote && !this.isOnTaskMode && this.isAnEmailChannel
      );
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        !this.isOnTaskMode &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    taskAgentOptions() {
      return this.agentList || [];
    },
    activeTask() {
      return this.currentChat.active_task || null;
    },
    showTaskBanner() {
      return !!this.activeTask || this.isTaskCompleting;
    },
    activeTaskAssigneeName() {
      if (this.completedTaskDisplay) {
        return this.completedTaskDisplay.assigneeName;
      }
      return (
        this.activeTask?.assignee?.name ||
        this.$t('CONVERSATION.REPLYBOX.TASK_FORM.ALL_ASSIGNEES')
      );
    },
    activeTaskDueAtLabel() {
      if (this.completedTaskDisplay) {
        return this.completedTaskDisplay.dueAtLabel;
      }
      if (!this.activeTask?.due_at) {
        return '';
      }

      return format(new Date(this.activeTask.due_at), 'dd/MM');
    },
    activeTaskNote() {
      if (this.completedTaskDisplay) {
        return this.completedTaskDisplay.note;
      }
      return this.activeTask?.note || '';
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      // Don't flip the composer out of task mode while the optimistic
      // "task completed" animation is still playing on the same conversation.
      if (this.isTaskCompleting && oldConversation?.id === conversation.id) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
      this.initializeTaskForm();
      this.resetAndFetchCopilotSuggestion();
    },
    copilotRequestKey() {
      this.resetAndFetchCopilotSuggestion();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
      if (updatedReplyType === REPLY_EDITOR_MODES.TASK) {
        this.initializeTaskForm();
      }
    },
  },

  mounted() {
    this.getFromDraft();
    this.initializeTaskForm();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
    this.fetchCopilotSuggestionDebounced = debounce(
      this.fetchRemoteCopilotSuggestion,
      700
    );

    this.fetchAndSetReplyTo();
    this.resetAndFetchCopilotSuggestion();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  methods: {
    resetAndFetchCopilotSuggestion() {
      this.aiCopilotSuggestion = null;
      this.aiCopilotSuggestionSource = null;
      if (
        !this.currentChat?.id ||
        this.currentChat.status !== wootConstants.STATUS_TYPE.OPEN
      ) {
        return;
      }
      this.fetchCopilotSuggestionDebounced();
    },
    async fetchRemoteCopilotSuggestion() {
      if (!this.currentChat?.id || !this.lastCustomerMessageText) return;

      this.isFetchingCopilotSuggestion = true;
      try {
        const { data } = await ConversationApi.getCopilotSuggestion(
          this.currentChat.id
        );
        const suggestion = data?.suggestion;
        if (suggestion?.type) {
          this.aiCopilotSuggestion = {
            ...suggestion,
            messageId: this.lastCustomerMessage?.id,
          };
          this.aiCopilotSuggestionSource = data?.source;
        }
      } catch (error) {
        // Keep the deterministic suggestion visible when AI is unavailable.
        this.aiCopilotSuggestion = null;
        this.aiCopilotSuggestionSource = 'deterministic';
      } finally {
        this.isFetchingCopilotSuggestion = false;
      }
    },
    async suggestManualReply() {
      if (this.isGeneratingManualReplySuggestion || !this.currentChat?.id) {
        return;
      }

      this.isGeneratingManualReplySuggestion = true;
      try {
        const { data } = await ConversationApi.getCopilotSuggestion(
          this.currentChat.id,
          { intent: 'reply_suggestion' }
        );
        const suggestedText = data?.suggestion?.action?.text;
        if (suggestedText) {
          this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
          this.replaceText(suggestedText);
        } else {
          useAlert('Não foi possível gerar uma resposta agora.');
        }
      } catch (error) {
        useAlert('Não foi possível gerar uma resposta agora.');
      } finally {
        this.isGeneratingManualReplySuggestion = false;
      }
    },
    dismissCopilotSuggestion() {
      this.dismissedCopilotSuggestionKey = this.copilotSuggestionKey;
      if (
        !this.appliedCopilotSuggestionKeys.includes(this.copilotSuggestionKey)
      ) {
        this.appliedCopilotSuggestionKeys.push(this.copilotSuggestionKey);
      }
    },
    openConversationPanel(panel) {
      const baseState = {
        is_contact_sidebar_open: false,
        is_copilot_panel_open: false,
        is_olist_panel_open: false,
        is_product_catalog_panel_open: false,
      };
      this.updateUISettings({
        ...baseState,
        [panel]: true,
      });
    },
    async applyCopilotLabel(labelTitle) {
      const suggestedLabel = (this.accountLabels || []).find(
        label => label.title === labelTitle
      );
      if (!suggestedLabel || this.currentChat.labels?.includes(labelTitle))
        return;

      const preservedLabels = (this.currentChat.labels || []).filter(
        label =>
          !this.accountLabels.some(accountLabel => accountLabel.title === label)
      );
      const nextLabels = [...preservedLabels, suggestedLabel.title];
      await this.$store.dispatch('conversationLabels/update', {
        conversationId: this.currentChat.id,
        labels: nextLabels,
      });
      this.$store.commit(types.UPDATE_CONVERSATION, {
        ...this.currentChat,
        labels: nextLabels,
      });
    },
    async applyCopilotSuggestion() {
      const suggestion = this.copilotSuggestionCandidate;
      if (!suggestion) return;

      const customerName = this.currentContact?.name || 'cliente';

      if (suggestion.type === 'set_urgent_priority') {
        await this.$store.dispatch('assignPriority', {
          conversationId: this.currentChat.id,
          priority: 'urgent',
        });
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'fill_conversation_attributes') {
        const customAttributes = suggestion.action?.custom_attributes || {};
        if (!Object.keys(customAttributes).length) {
          this.dismissCopilotSuggestion();
          return;
        }
        await this.$store.dispatch('updateCustomAttributes', {
          conversationId: this.currentChat.id,
          customAttributes: {
            ...(this.currentChat.custom_attributes || {}),
            ...customAttributes,
          },
        });
        useAlert('Campos da conversa atualizados.');
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'fill_contact_attributes') {
        const customAttributes = suggestion.action?.custom_attributes || {};
        if (!Object.keys(customAttributes).length || !this.currentContact?.id) {
          this.dismissCopilotSuggestion();
          return;
        }
        await this.$store.dispatch('contacts/update', {
          id: this.currentContact.id,
          customAttributes: {
            ...(this.currentContact.custom_attributes || {}),
            ...customAttributes,
          },
        });
        useAlert('Campos do contato atualizados.');
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'open_contact_profile') {
        this.openConversationPanel('is_contact_sidebar_open');
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'execute_macro') {
        const suggestedMacro = suggestion.action?.macro_id
          ? (this.macros || []).find(
              macro => Number(macro.id) === Number(suggestion.action.macro_id)
            )
          : this.matchingMacro;
        if (!suggestedMacro) {
          this.dismissCopilotSuggestion();
          return;
        }
        await this.$store.dispatch('macros/execute', {
          macroId: suggestedMacro.id,
          conversationIds: [this.currentChat.id],
        });
        await this.$store.dispatch('getConversation', this.currentChat.id);
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'assign_label') {
        const suggestedLabel =
          (this.accountLabels || []).find(
            label => label.title === suggestion.action?.label_title
          ) || this.matchingLabel;
        if (!suggestedLabel) {
          this.dismissCopilotSuggestion();
          return;
        }
        await this.applyCopilotLabel(suggestedLabel.title);
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'payment_received') {
        await this.applyCopilotLabel(suggestion.action?.label_title);
        this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
        this.replaceText(suggestion.action?.text);
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'create_task') {
        this.setReplyMode(REPLY_EDITOR_MODES.TASK);
        this.taskDueAt = addDays(
          new Date(),
          suggestion.action?.due_in_days || 1
        );
        this.taskNote =
          suggestion.action?.note ||
          `Retornar contato com ${customerName} sobre: ${this.lastCustomerMessageText}`;
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'create_schedule_task') {
        this.setReplyMode(REPLY_EDITOR_MODES.TASK);
        this.taskDueAt = addDays(
          new Date(),
          suggestion.action?.due_in_days || 1
        );
        this.taskNote =
          suggestion.action?.note ||
          `Organizar agendamento com ${customerName}: ${this.lastCustomerMessageText}`;
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'team_note') {
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
        this.replaceText(
          `Time, conseguem validar esse ponto para seguirmos com o atendimento?\n\nContexto do cliente: ${this.lastCustomerMessageText}`
        );
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'quick_reply_pix') {
        this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
        const pixResponse = this.matchingPixCannedResponse;
        this.replaceText(
          suggestion.action?.text ||
            pixResponse?.content ||
            `Olá, ${customerName}. Posso te enviar as opções de pagamento por PIX ou link de pagamento. Qual prefere?`
        );
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'create_cart') {
        this.requestCatalogAction({
          ...(suggestion.action || {}),
          search:
            suggestion.action?.search_query ||
            suggestion.action?.text ||
            this.lastCustomerMessageText,
        });
        this.openConversationPanel('is_product_catalog_panel_open');
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'lookup_orders') {
        this.requestCatalogAction({ open_orders: true });
        this.openConversationPanel('is_product_catalog_panel_open');
        this.dismissCopilotSuggestion();
        return;
      }

      if (suggestion.type === 'resolve_conversation') {
        this.$store.dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status: wootConstants.STATUS_TYPE.RESOLVED,
        });
        this.dismissCopilotSuggestion();
        return;
      }

      this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
      if (!suggestion.action?.text) {
        this.dismissCopilotSuggestion();
        return;
      }
      await this.applyCopilotLabel(suggestion.action?.label_title);
      this.replaceText(suggestion.action.text);
      this.dismissCopilotSuggestion();
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (replyType === REPLY_EDITOR_MODES.TASK) {
        return;
      }
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.replyType === REPLY_EDITOR_MODES.TASK) {
        return;
      }
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature)
        : removeSignature(message, this.messageSignature);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.onFileUpload({ name, type, size, file: file });
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
        // Although Chatwoot combines these into a single message, Instagram sends separate echo events for each component.
        // This can create duplicate messages in Chatwoot. To prevent this issue, we'll handle text and attachments as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        if ((isOnWhatsApp || isOnInstagram) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(this.message);
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popOutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      if (this.isOnTaskMode) {
        await this.saveTask();
        return;
      }

      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    initializeTaskForm() {
      const activeTask = this.currentChat.active_task || null;
      const dueAt = activeTask?.due_at
        ? new Date(activeTask.due_at)
        : addDays(new Date(), 1);
      const note = activeTask?.note || '';
      const assigneeId = activeTask?.assignee?.id ?? null;
      const selectedAssignee = this.taskAgentOptions.find(
        agent => agent.id === assigneeId
      );

      this.taskDueAt = dueAt;
      this.taskNote = note || '';
      this.taskAssignee = selectedAssignee
        ? { id: selectedAssignee.id, name: selectedAssignee.name }
        : {
            id: null,
            name: this.$t('CONVERSATION.REPLYBOX.TASK_FORM.ALL_ASSIGNEES'),
          };
    },
    getDefaultReplyMode() {
      const { can_reply: canReply } = this.currentChat;
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        return REPLY_EDITOR_MODES.REPLY;
      }

      return REPLY_EDITOR_MODES.NOTE;
    },
    async saveTask() {
      if (!this.taskDueAt || !this.taskNote?.trim()) {
        return;
      }

      this.isSavingTask = true;
      try {
        const payload = {
          conversationId: this.currentChat.id,
          dueAt: new Date(this.taskDueAt).toISOString(),
          note: this.taskNote.trim(),
          assigneeId: this.taskAssignee?.id ?? null,
        };
        const response = this.currentChat.active_task
          ? await ConversationApi.updateTask(payload)
          : await ConversationApi.createTask(payload);

        this.$store.commit(types.UPDATE_CONVERSATION, response.data);
        emitter.emit('conversation:tasks_updated');

        useAlert(this.$t('CONVERSATION.REPLYBOX.TASK_FORM.SUCCESS'));
        this.setReplyMode(this.getDefaultReplyMode());
      } catch (error) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.TASK_FORM.ERROR'));
      } finally {
        this.isSavingTask = false;
      }
    },
    async completeTask() {
      if (!this.activeTask?.id || this.isTaskCompleting) {
        return;
      }

      const taskId = this.activeTask.id;
      // Optimistically mark the task as done right away, the strike-through
      // and check animation shouldn't wait on the API response. The active
      // task gets cleared from the store as soon as the API call resolves,
      // so snapshot what's on screen to keep it visible through the animation.
      this.completedTaskDisplay = {
        assigneeName: this.activeTaskAssigneeName,
        dueAtLabel: this.activeTaskDueAtLabel,
        note: this.activeTaskNote,
      };
      this.isTaskCompleting = true;
      try {
        await ConversationApi.completeTaskById(taskId);
        await this.$store.dispatch('getConversation', this.currentChat.id);
        emitter.emit('conversation:tasks_updated');
        useAlert(this.$t('CONVERSATION.REPLYBOX.TASK_FORM.COMPLETE_SUCCESS'));

        // Let the check + strike-through animation play before the banner clears.
        await new Promise(resolve => {
          setTimeout(resolve, 900);
        });
        this.initializeTaskForm();
        this.setReplyMode(this.getDefaultReplyMode());
      } catch (error) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.TASK_FORM.COMPLETE_ERROR'));
      } finally {
        this.isTaskCompleting = false;
        this.completedTaskDisplay = null;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(message) {
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        message = appendSignature(message, this.messageSignature);
      }

      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      this.$nextTick(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
        this.onFocus();
      });
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      const canUseReplyMode =
        canReply || this.isAWhatsAppChannel || this.isAPIInbox;
      if (mode === REPLY_EDITOR_MODES.REPLY && !canUseReplyMode) {
        return;
      }

      this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        this.message = appendSignature(this.message, this.messageSignature);
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = this.isAnInstagramChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram, we need a separate text message
      // For WhatsApp, we only need a text message if there are no attachments
      if (
        (this.isAnInstagramChannel && this.message) ||
        (!this.isAnInstagramChannel && hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <Transition name="copilot-suggestion">
    <button
      v-if="shouldShowCopilotSuggestion"
      type="button"
      class="copilot-suggestion"
      @click="applyCopilotSuggestion"
    >
      <span class="copilot-suggestion__icon" aria-hidden="true">
        <span class="i-woot-captain size-4" />
      </span>
      <span class="copilot-suggestion__content">
        <span class="copilot-suggestion__title">
          {{ copilotSuggestionCandidate.title }}
        </span>
        <span class="copilot-suggestion__dash" aria-hidden="true" />
        <span class="copilot-suggestion__description">
          {{ copilotSuggestionCandidate.description }}
        </span>
      </span>
      <span class="i-lucide-check size-4 shrink-0 text-n-slate-10" />
    </button>
  </Transition>
  <div
    ref="replyEditor"
    class="reply-box"
    :class="[replyBoxClass, { 'is-audio-mode': showAudioRecorderEditor }]"
  >
    <div
      v-if="showTaskBanner"
      class="mx-6 mt-3 mb-0 flex items-start justify-between gap-3 px-2 py-1 text-n-amber-11"
    >
      <div class="min-w-0">
        <p
          class="text-xs font-semibold uppercase tracking-wide text-n-amber-10"
        >
          {{ $t('CONVERSATION.REPLYBOX.TASK_FORM.ACTIVE_TASK') }}
        </p>
        <p
          class="text-sm truncate text-n-amber-12 transition-all duration-300"
          :class="{ 'line-through opacity-50': isTaskCompleting }"
        >
          {{
            $t('CONVERSATION.REPLYBOX.TASK_FORM.ACTIVE_TASK_ASSIGNEE', {
              assignee: activeTaskAssigneeName,
              dueAt: activeTaskDueAtLabel,
            })
          }}
        </p>
        <p
          v-if="activeTaskNote"
          class="mt-1 line-clamp-2 break-words text-xs text-n-amber-11 transition-all duration-300"
          :class="{ 'line-through opacity-50': isTaskCompleting }"
        >
          {{ activeTaskNote }}
        </p>
      </div>
      <button
        type="button"
        class="task-complete-button grid size-10 shrink-0 place-content-center self-center rounded-full border transition disabled:cursor-wait"
        :class="
          isTaskCompleting
            ? 'task-complete-button--done border-n-teal-9 bg-n-teal-9 text-white'
            : 'border-n-amber-8/60 bg-n-amber-3/60 text-n-amber-10 hover:bg-n-amber-4'
        "
        :disabled="isTaskCompleting"
        :title="$t('CONVERSATION.REPLYBOX.TASK_FORM.COMPLETE')"
        @click="completeTask"
      >
        <span class="i-lucide-check size-5" />
      </button>
    </div>
    <ReplyTopPanel
      v-if="false"
      :mode="replyType"
      :is-reply-restricted="isReplyRestricted"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popOutReplyBox"
      @set-reply-mode="setReplyMode"
      @toggle-popout="togglePopout"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <div
      v-if="
        shouldShowReplyToMessage || (hasAttachments && !showAudioRecorderEditor)
      "
      class="reply-box__context"
      @paste="onPaste"
    >
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <AttachmentPreview
        v-if="hasAttachments && !showAudioRecorderEditor"
        :attachments="attachedFiles"
        variant="large"
        @remove-attachment="removeAttachment"
      />
    </div>
    <div class="reply-box__top">
      <EmojiInput
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="{
          'emoji-dialog--expanded': isOnExpandedLayout || popOutReplyBox,
        }"
        :on-click="addIntoEditor"
      />
      <ReplyEmailHead
        v-if="showReplyHead"
        v-model:cc-emails="ccEmails"
        v-model:bcc-emails="bccEmails"
        v-model:to-emails="toEmails"
      />
      <div v-if="showAudioRecorderEditor" class="audio-recorder-slot">
        <AudioRecorder
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
      </div>
      <template v-else-if="isOnTaskMode">
        <TaskComposer
          v-model:due-at="taskDueAt"
          v-model:assignee="taskAssignee"
          :agents="taskAgentOptions"
        />
        <WootMessageEditor
          v-model="taskNote"
          :editor-id="`${editorStateId}-task`"
          class="input popover-prosemirror-menu"
          :placeholder="$t('CONVERSATION.REPLYBOX.TASK_FORM.NOTE_PLACEHOLDER')"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="1"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
        />
      </template>
      <WootMessageEditor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input popover-prosemirror-menu"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="1"
        enable-variables
        :variables="messageVariables"
        :signature="messageSignature"
        allow-signature
        :channel-type="channelType"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
      <QuotedEmailPreview
        v-if="shouldShowQuotedPreview"
        :quoted-email-text="quotedEmailText"
        :preview-text="quotedEmailPreviewText"
        @toggle="toggleQuotedReply"
      />
    </div>
    <ReplyBottomPanel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :enable-whats-app-templates="showWhatsappTemplates"
      :enable-content-templates="showContentTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :is-note="isPrivate"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :conversation-type="conversationType"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
      :quoted-reply-enabled="quotedReplyPreference"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :message="message"
      :portal-slug="connectedPortalSlug"
      :new-conversation-modal-active="newConversationModalActive"
      :reply-mode="replyType"
      @set-reply-mode="setReplyMode"
      @select-whatsapp-template="openWhatsappTemplateModal"
      @select-content-template="openContentTemplateModal"
      @replace-text="replaceText"
      @toggle-insert-article="toggleInsertArticle"
      @toggle-quoted-reply="toggleQuotedReply"
      @suggest-reply="suggestManualReply"
    />
    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
  <MessageSignatureMissingAlert
    v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
  />
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.task-complete-button--done {
  animation: task-complete-pop 320ms ease;
}

.copilot-suggestion {
  @apply relative z-[99999] mx-14 mb-2 flex min-h-8 items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-n-alpha-1 hover:shadow-sm active:translate-y-0 active:scale-[0.995] dark:hover:bg-n-alpha-2;
}

.copilot-suggestion__icon {
  @apply grid size-6 shrink-0 place-content-center rounded-lg bg-n-alpha-1 text-n-slate-11 dark:bg-n-solid-3;
}

.copilot-suggestion__content {
  @apply flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm leading-5;
}

.copilot-suggestion__title {
  @apply shrink-0 font-medium text-n-blue-text hover:underline;
}

.copilot-suggestion__dash {
  @apply shrink-0 text-n-slate-10;

  &::before {
    content: '—';
  }
}

.copilot-suggestion__description {
  @apply min-w-0 truncate text-n-slate-10;
}

.copilot-suggestion-enter-active,
.copilot-suggestion-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.copilot-suggestion-enter-from,
.copilot-suggestion-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.985);
}

@keyframes task-complete-pop {
  0% {
    transform: scale(0.85);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  transition:
    height 180ms ease,
    box-shadow 180ms ease,
    border-color 180ms ease;

  @apply relative z-[100000] mb-5 mx-8 min-h-[4.25rem] overflow-visible rounded-[2rem] border border-n-weak bg-white/95 backdrop-blur-xl dark:bg-n-solid-2/95;
  box-shadow: 0 18px 44px rgb(15 23 42 / 10%);

  &.is-focused {
    @apply border-n-slate-5;
    box-shadow: 0 24px 72px rgb(15 23 42 / 16%);
  }

  &.is-private {
    @apply bg-n-amber-2/90 dark:border-n-amber-3/10 border-n-amber-12/10;
  }

  &.is-task-mode {
    @apply bg-n-teal-2/90 dark:border-n-teal-3/10 border-n-teal-12/10;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative z-10 flex min-h-[4.25rem] items-center py-0 pl-[5.25rem] pr-[16.75rem];
}

.reply-box__context {
  @apply relative z-10 mx-4 mt-4 flex flex-col gap-3 rounded-[1.5rem] bg-white/70 p-3 dark:bg-n-solid-3/70;
}

.reply-box__context :deep(.reply-editor) {
  @apply m-0 rounded-2xl bg-n-alpha-1 px-3 py-2;
}

.reply-box__top :deep(.input),
.reply-box__top :deep(.ProseMirror-menubar-wrapper) {
  @apply mb-0 w-full gap-0;
}

.reply-box.is-audio-mode .reply-box__top {
  @apply h-[4.25rem] min-h-[4.25rem] items-center pl-[5.25rem] pr-[19.5rem];
}

.reply-box.is-task-mode .reply-box__top {
  @apply flex-col items-stretch gap-2 py-3 pl-[5.25rem] pr-[13.5rem];
}

.audio-recorder-slot {
  @apply flex h-9 w-full min-w-0 items-center overflow-hidden rounded-full bg-n-alpha-1 px-3;
}

.audio-recorder-slot :deep(> div) {
  @apply h-9 w-full p-0;
}

.audio-recorder-slot :deep(wave) {
  @apply overflow-hidden rounded-full;
  height: 2.25rem !important;
}

.audio-recorder-slot :deep(canvas) {
  height: 2.25rem !important;
}

.reply-box__top :deep(.ProseMirror) {
  @apply min-h-8 w-full overflow-y-auto py-0 text-base leading-8;
  max-height: 7.5rem;
  scrollbar-width: none;
}

.reply-box__top :deep(.ProseMirror-woot-style) {
  min-height: 2rem !important;
}

.reply-box__top :deep(.ProseMirror::-webkit-scrollbar) {
  display: none;
}

.reply-box__top :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  @apply truncate text-n-slate-10;
  max-width: calc(100% - 0.5rem);
}

.reply-box__top :deep(.ProseMirror p),
.reply-box__top :deep(.popover-prosemirror-menu .ProseMirror p:last-child) {
  margin-block: 0 !important;
  line-height: 2rem !important;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
